<template>
  <div>
    <!-- Error State: Report Not Found -->
    <div v-if="hasError" class="d-flex flex-column align-items-center justify-content-center py-5">
      <feather-icon
        icon="AlertCircleIcon"
        size="64"
        class="text-danger mb-2"
      />
      <h2 class="mb-1">Report Not Found</h2>
      <p class="text-muted mb-2">The report you are looking for does not exist or could not be loaded.</p>
      <b-button
        variant="primary"
        @click="$router.go(-1)"
      >
        Go Back
      </b-button>
    </div>

    <div v-if="internalEndpointError" class="d-flex flex-column align-items-center justify-content-center py-5">
      <feather-icon
        icon="CloudIcon"
        size="64"
      />
      <h2 class="mb-1">Oops! Something went wrong</h2>
      <p class="text-muted mb-2">We are having difficulties loading the report you're looking for.
    </p>
      <b-button
        variant="primary"
        @click="$router.go(-1)"
      >
        Go Back
      </b-button>
    </div>

    <div v-else-if="limitConditionMet" class="d-flex flex-column align-items-center justify-content-center py-5">
      <feather-icon
        icon="AlertCircleIcon"
        size="64"
        class="text-danger mb-2"
      />
      <h2 class="mb-1">Account limit reached</h2>
      <p>{{ limitConditionMessage }}</p>
      <b-button
        variant="primary"
        @click="$router.go(-1)"
      >
        Go Back
      </b-button>
    </div>

    <!-- Main Content -->
    <!-- <div v-else-if="!isLoadingReportAccess && !hasError"> -->
    <div v-else-if="((reportFlavour === 'bespoke' || reportFlavour === 'draft') && draftReady) || (!isLoadingReportAccess && !hasError)">
      <product-breakdown-sticky-bar
        :report-title="reportTitle"
        :product-type-icon="productTypeIcon"
        :product-type-icon-color="productTypeIconColor"
        :product-state="productState"
        :report-number="reportNumber"
        :is-generating-pdf="isPdfDisabled"
        @generate-pdf="generatePdf"
      />

      <!--- Overview Section -->
      <b-row id="overview"
             class="mb-0">
        <b-col cols="12">
          <b-card v-if="isLoading">
            <LoadingContainer />
          </b-card>
          <product-overview
            v-else
            :report-number="reportNumber"
            :report-title="reportTitle"
            :product-type-icon="productTypeIcon"
            :product-type-icon-color="productTypeIconColor"
            :provider-information="providerInformation"
            :key-info="keyInfo"
            :top-box="sidebar.topbox"
            :details-id="detailsID"
            :product-state="productState"
            :report-structure-data="reportStructureData"
            :is-generating-pdf="isPdfDisabled"
            @add-to-portfolio="addProductToPortfolio.show = true"
            @generate-pdf="generatePdf"
          />
        </b-col>
      </b-row>

      <!--- Navigation Bar -->
      <b-row id="static-nav-bar">
        <b-col cols="12">
          <product-breakdown-nav />
        </b-col>
      </b-row>

      <!--- Key Information Section -->
      <b-row id="key-information">
        <b-col cols="12">
          <h1 class="mb-2 mt-4">Key Information</h1>
          <b-card v-if="isLoading">
            <LoadingContainer />
          </b-card>
          <product-key-information
            v-else
            :key-info="keyInfo"
            :top-box="sidebar.topbox"
            :product-summary="sidebar.productSummary"
            :report-number="reportNumber"
            :details-id="detailsID"
            :product-type-guide-id="productTypeGuideId"
            :product-type-id="productTypeId"
            :provider-id="providerInformation.providerId"
          />
        </b-col>
      </b-row>

      <!--- Documentation Section -->
      <b-row id="documentation">
        <b-col cols="12">
          <h1 class="mb-2 mt-2">Documentation</h1>
          <LoadingContainer v-if="isLoading" />
          <product-documentation
            v-else
            :documents="documents"
            :disabled="productDocumentationAccess.disabled"
            :unavailable-message="productDocumentationAccess.message"
            @download-document="addProductDownloadHit"
          />
        </b-col>
      </b-row>

      <!--- Analysis Section -->
      <b-row id="analysis">
        <b-col cols="12">
          <h1 class="mb-2 mt-2">Analysis</h1>

          <b-card v-if="isLoading">
            <LoadingContainer />
          </b-card>
          <product-analysis
            v-else
            ref="productAnalysis"
            :key-info="keyInfo"
            :figure-one="figureOne"
            :figure-two="figureTwo"
            :figure-three="figureThree"
            :figure-four="figureFour"
            :figure-five="figureFive"
            :figure-six="figureSix"
            :payoff-chart-data="payoffChartData"
            :return-chart-abs="returnChartAbs"
            :return-chart-ann="returnChartAnn"
            :capital-data="capitalData"
            :has-lifecycle-data="hasLifecycleData"
            :product-state="productState"
            :termination-date="reportInfo.TERMINATION && reportInfo.TERMINATION.TERMINATIONDATE ? reportInfo.TERMINATION.TERMINATIONDATE : null"
          />
        </b-col>
      </b-row>

      <!--- Compliance Section -->
      <b-row id="compliance">
        <b-col cols="12">
          <div class="mb-2 mt-2 d-flex align-items-center">
            <h1 class="mb-0">Compliance</h1>
            <hint-popup
              :id="17"
              class="ml-50"
            />
            <b-button
              v-if="isHiddenByProductStatus && showComplianceAnyway"
              variant="outline-secondary"
              size="sm"
              class="ml-auto flex-shrink-0"
              aria-label="Hide Compliance Information"
              @click="showComplianceAnyway = false"
            >
              <feather-icon
                icon="EyeOffIcon"
                size="14"
                class="mr-50"
              />
              Hide
            </b-button>
          </div>

          <!-- Hidden by default for live/matured products -->
          <b-card v-if="isHiddenByProductStatus && !showComplianceAnyway" class="text-center">
            <p class="mb-1">
              This product is <strong>{{ productState }}</strong>.<br> Compliance Information is only shown by default for current products.
            </p>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="showComplianceAnyway = true"
            >
              <feather-icon icon="EyeOffIcon" size="14" class="mr-50" />
              Show Compliance Information
            </b-button>
          </b-card>

          <template v-else>
            <!-- <div v-if="reportFlavour === 'draft' && !internalDraft"> -->
            <div v-if="reportFlavour === 'draft'">
              <draft-blocking-overlay />
            </div>
            <div v-else-if="reportFlavour === 'bespoke'">
              <bespoke-blocking-overlay />
            </div>
            <div v-else-if="isPremium">
              <product-compliance
                ref="productCompliance"
                :report-number="reportNumber"
                :preference-info="preferenceInfo"
                :is-loading-preference="isLoadingPreference"
                :preference-error="preferenceError"
                :target-market-info="targetMarketInfo"
                :is-loading-target-market="isLoadingTargetMarket"
                :target-market-error="targetMarketError"
                :suitability-info="suitabilityInfo"
                :is-loading-suitability="isLoadingSuitability"
                :suitability-error="suitabilityError"
                :selected-preference-id="selectedPreferenceId"
                @preference-changed="selectedPreferenceId = $event"
              />
            </div>
            <div v-else>
              <UpgradeToPremium />
            </div>
          </template>

        </b-col>
      </b-row>

      <!--- Lifecycle Section -->
      <b-row
        id="lifecycle"
      >
        <b-col cols="12">
          <div class="d-flex align-items-center mb-2 mt-2">
            <h1 class="mb-0">Lifecycle</h1>
            <b-badge v-if="productState === 'matured'" variant="light-warning" pill class="ml-1" style="font-size: 1rem;">Matured</b-badge>
          </div>
          <div v-if="reportFlavour === 'draft'">
            <draft-blocking-overlay />
          </div>
          <div v-else-if="reportFlavour === 'bespoke'">
            <bespoke-blocking-overlay />
          </div>
          <div v-else-if="isPremium && lfcIsActive">
            <product-lifecycle
              v-if="!isLoading"
              ref="productLifecycle"
              :underlying-table="underlyingTable"
              :product-snapshot="productSnapshot"
              :product-id="reportNumber"
              :capital-data="capitalData"
              :has-lifecycle-data="hasLifecycleData"
              :product-state="productState"
              :termination-date="reportInfo.TERMINATION && reportInfo.TERMINATION.TERMINATIONDATE ? reportInfo.TERMINATION.TERMINATIONDATE : null"
              :maturity-info="maturityInfo"
              @events-loading-changed="isEventsChartLoading = $event"
            />
          </div>
          <div v-else-if="isPremium">
            <b-alert show variant="warning" class="mt-3">
              <div class="alert-body">
                <feather-icon icon="AlertTriangleIcon" class="mr-50" />
                <span>Lifecycle information is not available for this product.</span>
              </div>
            </b-alert>
          </div>
          <div v-else>
            <UpgradeToPremium />
          </div>
        </b-col>
      </b-row>

      <!--- Provider Section -->
      <b-row id="provider">
        <b-col cols="12">
          <h1 class="mb-2 mt-2">Provider Information</h1>

          <product-provider
            :provider-information="providerInformation"
            :documents="documents"
            :report-title="reportTitle"
            :details-id="detailsID"
            :report-flavour="reportFlavour"
          />
        </b-col>
      </b-row>

      <!-- Always-mounted hidden PreferenceAnalysis for PDF chart pre-rendering -->
      <div style="display:none;">
        <preference-analysis
          ref="preferenceAnalysis"
          :preference-info="preferenceInfo"
          :suitability-info="suitabilityInfo"
          :selected-preference-id="selectedPreferenceId"
          mode="single"
        />
      </div>

      <!-- Add Product to Portfolio Modal -->
      <b-modal
        v-model="addProductToPortfolio.show"
        centered
        title="Add Product To Portfolio"
        hide-footer
      >
        <add-product-to-portfolio-modal-content
          :product="{ productid: reportNumber, productname: reportTitle }"
          @portfolioUpdated="onProductAddedToPortfolio"
        />
      </b-modal>
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BBadge, BButton, BModal, BAlert,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import Chart from 'chart.js'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import HintPopup from '@/views/components/HintPopup.vue'
import UpgradeToPremium from '@/views/components/UpgradeToPremium.vue'
import AddProductToPortfolioModalContent from '@/views/components/AddProductToPortfolioModalContent.vue'
import ProductOverview from './breakdown/ProductOverview.vue'
import ProductBreakdownNav from './breakdown/ProductBreakdownNav.vue'
import ProductBreakdownStickyBar from './breakdown/ProductBreakdownStickyBar.vue'
import ProductKeyInformation from './breakdown/ProductKeyInformation.vue'
import ProductAnalysis from './breakdown/ProductAnalysis.vue'
import ProductLifecycle from './breakdown/ProductLifecycle.vue'
import ProductDocumentation from './breakdown/ProductDocumentation.vue'
import ProductCompliance from './breakdown/ProductCompliance.vue'
import ProductProvider from './breakdown/ProductProvider.vue'
import PreferenceAnalysis from './breakdown/PreferenceAnalysis.vue'
import DraftBlockingOverlay from '../components/DraftBlockingOverlay.vue'
import BespokeBlockingOverlay from '../components/BespokeBlockingOverlay.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BBadge,
    BButton,
    BModal,
    BAlert,
    AddProductToPortfolioModalContent,
    ProductOverview,
    ProductBreakdownNav,
    ProductBreakdownStickyBar,
    ProductKeyInformation,
    ProductAnalysis,
    ProductLifecycle,
    ProductDocumentation,
    ProductCompliance,
    ProductProvider,
    PreferenceAnalysis,
    UpgradeToPremium,
    LoadingContainer,
    HintPopup,
    DraftBlockingOverlay,
    BespokeBlockingOverlay,
  },
  props: {
    reportFlavour: {
      type: String,
      default: 'standard',
    },
    fvcdetailsid: {
      type: [String, Number],
      default: undefined,
    },
    suppliedPassword: {
      type: String,
      default: undefined,
    },
    internalDraft: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Loading and error states
      draftReady: false,
      isLoadingReportAccess: true,
      limitConditionMet: false,
      limitConditionMessage: undefined,
      isLoading: true,
      hasError: false,
      internalEndpointError: false,
      isLoadingTargetMarket: false,
      isLoadingPreference: false,
      isLoadingSuitability: false,
      targetMarketError: false,
      preferenceError: false,
      suitabilityError: false,
      targetMarketInfo: null,
      suitabilityInfo: null,
      selectedPreferenceId: null,
      // Testing variable: Set to new Date() for real-time data
      // Can be temporarily set to a specific date for testing product states
      // Example: new Date('2024-01-15') to simulate a specific date
      currentDate: new Date('2025-09-10'),
      isLink: ['Platform', 'Website'],
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      documents: {},
      productDocumentationAccess: {
        canAccess: true,
        disabled: false,
        message: '',
      },
      providerInformation: {
        contact: {},
        reportDisplay: {},
        name: '',
      },
      sidebar: {
        topbox: {},
        productSummary: [],
      },
      keyInfo: {
        leftTbl: {},
        rightTbl: {},
        bottomTbl: {},
      },
      figureOne: {
        headings: [],
        names: [],
        values: [],
      },
      figureTwo: {
        datapoints: {},
        chartsettings: {},
      },
      figureThree: {
        datapoints: {},
        thisproductdatapoint: {},
      },
      figureFour: {
        intro: '',
        isCompressed: '',
        headings: [],
        names: [],
        values: [],
      },
      figureFive: {
        intro: '',
        headings: [],
        names: [],
        values: [],
      },
      figureSix: {
        annualised: {},
        payoff: {},
      },
      maturityInfo: null,
      underlyingTable: {
        header: [],
        content: [],
      },
      productSnapshot: {
        header: [],
        content: [],
      },
      payoffChartData: {
        SHOWDATA: [],
      },
      returnChartAbs: {
        XAXIS: [],
        SHOWDATA: [],
      },
      returnChartAnn: {
        XAXIS: [],
        SHOWDATA: [],
      },
      capitalData: {
        SHOWDATA: {
          DATA: [],
        },
      },
      hasLifecycleData: false,
      preferenceInfo: null,
      // Product state tracking:
      // - 'current': Product is before its closing date, still available for investment
      // - 'early_live': Product has closed but lifecycle data is not yet available
      // - 'live': Product has closed and lifecycle data is available for tracking
      // - 'matured': Product has reached maturity/termination
      addProductToPortfolio: {
        show: false,
      },
      isGeneratingPdf: false,
      isEventsChartLoading: false,
      showAnalysisAnyway: false,
      showComplianceAnyway: false,
      productState: 'current',
      reportInfo: {
        TERMINATION: {
          ISTERMINATED: false,
        },
      },
      reportStructureData: null,
      reportTitle: '',
      reportNumber: null,
      detailsID: '',
      productTypeIcon: '',
      productTypeIconColor: '',
      productTypeName: '',
      productTypeId: null,
      // Lifcycle flag to indicate if we are able to product lifecycle for the product
      lfcIsActive: false,
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isPremium',
    ]),
    isPdfDisabled() {
      return this.isGeneratingPdf
        || this.isEventsChartLoading
        || this.isLoadingSuitability
        || this.isLoadingTargetMarket
        || this.isLoadingPreference
    },
    isHiddenByProductStatus() {
      return this.productState === 'live' || this.productState === 'matured'
    },
  },
  watch: {
    preferenceInfo: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.selectedPreferenceId) {
          this.selectedPreferenceId = newVal[0].preferenceid
        }
      },
    },
    selectedPreferenceId(newVal) {
      if (newVal) this.loadSuitabilityInfo()
    },
  },
  async created() {
    switch (this.reportFlavour) {
      case 'standard':
        this.reportNumber = Number(this.$route.params.reportNumber)

        if (Number.isNaN(Number(this.reportNumber)) || Number(this.reportNumber) <= 0) {
          this.hasError = true
          this.isLoading = false
          return
        }

        await this.reportCanBeViewed()

        if (!this.limitConditionMessage && !this.hasError) {
          await this.loadSEData()
          this.isLoading = false

          if (this.isLoggedIn && this.isPremium) {
            this.loadPreferenceInfo()
            this.loadTargetMarketInfo()
          }
          this.addProductViewHit()
        }

        break
      case 'draft':
      case 'bespoke':
        await this.loadSEData()
        this.draftReady = true
        this.isLoading = false
        if (this.internalDraft) {
          this.loadPreferenceInfo()
          this.loadTargetMarketInfo()
        }
        break
      default:
        this.$router.push({ name: 'home' })
    }
  },
  mounted() {
    this.createPayoffChart()
    this.createAnnualisedBarChart()
    this.createPayoffBarChart()
  },
  methods: {
    generatePdf(reportType) {
      this.isGeneratingPdf = true
      this.addProductDownloadHit()

      const charts = {
        ...(this.$refs.productAnalysis ? this.$refs.productAnalysis.collectCharts() : {}),
        ...(this.$refs.preferenceAnalysis ? this.$refs.preferenceAnalysis.collectCharts() : {}),
        ...(this.$refs.productLifecycle ? this.$refs.productLifecycle.collectCharts() : {}),
      }

      const payload = JSON.stringify({
        charts,
        reportData: this.reportStructureData,
        suitabilityInfo: this.suitabilityInfo,
        targetMarketInfo: this.targetMarketInfo,
        ...(this.$refs.productLifecycle ? this.$refs.productLifecycle.collectData() : {}),
      })

      const tabName = `pdf-report-${this.reportNumber}-${reportType}`

      const form = Object.assign(document.createElement('form'), {
        method: 'POST',
        action: `${this.baseURL}cfc/dynamicreport/product/productpdf.cfm?reportNumber=${this.reportNumber}&reportType=${reportType}`,
        target: tabName,
      })

      const input = Object.assign(document.createElement('input'), {
        type: 'hidden',
        name: 'payload',
        value: payload,
      })

      form.appendChild(input)
      document.body.appendChild(form)

      window.open('', tabName)
      form.submit()

      form.remove()
      this.isGeneratingPdf = false
    },
    reportCanBeViewed() {
      this.isLoadingReportAccess = true
      return this.$http
        .get('reportstructure.cfc', {
          params: {
            method: 'isReportAccessible',
            reportnumber: this.reportNumber,
          },
        })
        .then(response => {
          const { status, lifecycle } = response.data

          this.lfcIsActive = lifecycle

          if (!status) {
            this.limitConditionMessage = response.data.message
            this.limitConditionMet = true
            this.isLoading = false
          }
          this.isLoadingReportAccess = false
        })
        .catch(() => {
          this.hasError = true
          this.isLoadingReportAccess = false
        })
    },
    updateProductState(responseData) {
      // Use the status field from the backend if available
      const status = responseData.status.toLowerCase()

      switch (status) {
        case 'pre-strike':
          this.productState = 'early_live'
          break
        default:
          this.productState = status
      }
    },
    addProductViewHit() {
      this.$http
        .get('hits.cfc', {
          params: {
            method: 'addProductReportHit',
            hitname: 'product_view',
            reportnum: this.reportNumber,
          },
        })

      if (String(this.productState).toLowerCase() === 'current') {
        this.$http
          .get('hits.cfc', {
            params: {
              method: 'addProductReportHit',
              hitname: 'product_view_current',
              reportnum: this.reportNumber,
            },
          })
      }

      if (String(this.$route.query.source).toLowerCase() === 'email') {
        this.$http
          .get('hits.cfc', {
            params: {
              method: 'addProductReportHit',
              hitname: 'product_view_email',
              reportnum: this.reportNumber,
            },
          })
      }
    },
    addProductDownloadHit() {
      this.$http
        .get('hits.cfc', {
          params: {
            method: 'addProductReportHit',
            hitname: 'product_download',
            reportnum: this.reportNumber,
          },
        })

      if (String(this.productState).toLowerCase() === 'current') {
        this.$http
          .get('hits.cfc', {
            params: {
              method: 'addProductReportHit',
              hitname: 'product_download_current',
              reportnum: this.reportNumber,
            },
          })
      }
    },
    loadPreferenceInfo() {
      if (!this.reportNumber) return Promise.resolve()

      this.isLoadingPreference = true
      this.preferenceError = false

      return this.$http
        .get('premium.cfc', {
          params: {
            method: 'getPreferenceByProductSingle',
            productIdIn: this.reportNumber,
          },
        })
        .then(response => {
          this.preferenceInfo = response.data
          this.isLoadingPreference = false
        })
        .catch(error => {
          console.error('Error loading preference info:', error)
          this.preferenceError = true
          this.isLoadingPreference = false
        })
    },
    loadTargetMarketInfo() {
      this.isLoadingTargetMarket = true
      this.targetMarketError = false
      return this.$http
        .get('premium.cfc', {
          params: { method: 'getTargetMarketInfo', productIdIn: this.reportNumber },
        })
        .then(response => {
          this.targetMarketInfo = response.data
          this.isLoadingTargetMarket = false
        })
        .catch(() => {
          this.targetMarketError = true
          this.isLoadingTargetMarket = false
        })
    },
    loadSuitabilityInfo() {
      if (!this.reportNumber || !this.selectedPreferenceId) return
      this.isLoadingSuitability = true
      this.suitabilityError = false
      this.$http
        .get('premium.cfc', {
          params: {
            method: 'getSuitabilityInfo',
            productIdIn: this.reportNumber,
            preferenceId: this.selectedPreferenceId,
            mode: 'single',
            showQuotientLines: 'false',
          },
        })
        .then(response => {
          if (!response.data.SUCCESS) {
            this.suitabilityError = true
            this.isLoadingSuitability = false
            return
          }
          this.suitabilityInfo = response.data
          this.isLoadingSuitability = false
        })
        .catch(() => {
          this.suitabilityError = true
          this.isLoadingSuitability = false
        })
    },
    loadSEData() {
      const requestConfig = {
        standard: {
          endpoint: 'reportstructure.cfc',
          params: {
            method: 'getreportstructure',
            mode: 'standard',
            reportcode: this.reportNumber,
          },
        },
        'draft-fvc': {
          endpoint: 'reportflavours.cfc',
          params: {
            method: 'getinternaldraft',
            fvcdetailsid: this.fvcdetailsid,
            fvcpassword: this.suppliedPassword,
          },
        },
        draft: {
          endpoint: 'reportflavours.cfc',
          params: {
            method: 'getproviderdraft',
            fvcdetailsid: this.fvcdetailsid,
            draftpassword: this.suppliedPassword,
          },
        },
        bespoke: {
          endpoint: 'reportflavours.cfc',
          params: {
            method: 'getbespokereport',
            fvcdetailsid: this.fvcdetailsid,
            linkpassword: this.suppliedPassword,
          },
        },
      }

      const config = requestConfig[this.internalDraft ? 'draft-fvc' : this.reportFlavour]

      if (!config) {
        return Promise.resolve()
      }

      return this.$http
        .get(config.endpoint, {
          params: config.params,
        })
        .then(response => {
          this.reportStructureData = response.data
          this.reportTitle = response.data.content.reportTitle
          this.detailsID = response.data.content.INTERNALINFO.FVCDETAILSID
          this.productTypeIcon = response.data.content.pticonname || 'BoxIcon'
          this.productTypeIconColor = response.data.content.pticoncolor || 'light-primary'
          this.productTypeGuideId = response.data.content.PTGUIDEID
          this.productTypeId = response.data.content.PRODUCTTYPEID
          this.productTypeName = response.data.content.producttypename || ''
          this.productTypeName = response.data.content.producttypename || ''
          this.reportNumber = Number(response.data.content.sidebar.topbox.Report)
          this.documents = response.data.content.productDocuments
          this.productDocumentationAccess = response.data.content.productDocumentationAccess || {
            canAccess: true,
            disabled: false,
            message: '',
          }
          this.providerInformation.reportDisplay = response.data.content.reportDisplay
          this.providerInformation.contact = response.data.content.sidebar.providerContact
          this.providerInformation.name = response.data.content.sidebar.providerContact.providerName
          this.providerInformation.providerId = response.data.content.sidebar.providerContact.ProviderId
          this.sidebar.topbox = response.data.content.sidebar.topbox
          this.sidebar.productSummary = response.data.content.sidebar.productSummary
          this.keyInfo.leftTbl = response.data.content.LHSCOL
          this.keyInfo.rightTbl = response.data.content.RHSCOL
          this.keyInfo.bottomTbl = response.data.content.FULLCOL
          this.figureOne = response.data.content.Assessment.FigureOne
          this.figureTwo = response.data.content.Assessment.FigureTwo
          this.figureThree = response.data.content.Assessment.FigureThree
          this.figureFour = response.data.content.Outcomes.FigureFour
          this.figureFour.isCompressed = response.data.content.FVCInfo.isCompressed
          this.figureFive = response.data.content.Outcomes.FigureFive
          this.maturityInfo = response.data.content.maturityinfo || null
          // eslint-disable-next-line prefer-destructuring
          this.figureSix.annualised = response.data.content.Outcomes.FigureSix.annualised
          // eslint-disable-next-line prefer-destructuring
          this.figureSix.payoff = response.data.content.Outcomes.FigureSix.payoff
          // Load reportInfo if available
          if (response.data.content.lifecycle_data && response.data.content.lifecycle_data !== 'no_data' && response.data.content.lifecycle_data.INFO) {
            this.reportInfo = { ...this.reportInfo, ...response.data.content.lifecycle_data.INFO }
          }

          // Determine product state
          this.updateProductState(response.data.content)

          // Check if lifecycle_data is "no_data"
          if (response.data.content.lifecycle_data === 'no_data') {
            this.hasLifecycleData = false
          } else {
            this.hasLifecycleData = true
            // Load lifecycle data (underlying asset table)
            if (response.data.content.lifecycle_data && response.data.content.lifecycle_data.DATA && response.data.content.lifecycle_data.DATA.statedata_underlyingtable) {
              this.underlyingTable = {
                header: response.data.content.lifecycle_data.DATA.statedata_underlyingtable.header || [],
                content: response.data.content.lifecycle_data.DATA.statedata_underlyingtable.content || [],
              }
            }
            // Load lifecycle data (product snapshot table)
            if (response.data.content.lifecycle_data && response.data.content.lifecycle_data.DATA && response.data.content.lifecycle_data.DATA.statedata_covertable) {
              const snapshotContent = response.data.content.lifecycle_data.DATA.statedata_covertable.content || []
              // Filter out the "Strike" row
              const filteredContent = snapshotContent.filter(row => {
                if (typeof row === 'object' && row !== null) {
                  // Check all values in the row object for "Strike"
                  const values = Object.values(row)
                  return !values.some(val => val === 'Strike')
                }
                return true
              })
              this.productSnapshot = {
                header: response.data.content.lifecycle_data.DATA.statedata_covertable.header || [],
                content: filteredContent,
              }
            }
            // Load lifecycle data (payoff chart)
            if (response.data.content.lifecycle_data && response.data.content.lifecycle_data.DATA && response.data.content.lifecycle_data.DATA.PAYOFFCHART) {
              this.payoffChartData = response.data.content.lifecycle_data.DATA.PAYOFFCHART
            }
            // Load lifecycle data (return charts for distribution)
            if (response.data.content.lifecycle_data && response.data.content.lifecycle_data.DATA && response.data.content.lifecycle_data.DATA.RETURNCHARTABS) {
              this.returnChartAbs = response.data.content.lifecycle_data.DATA.RETURNCHARTABS
            }
            if (response.data.content.lifecycle_data && response.data.content.lifecycle_data.DATA && response.data.content.lifecycle_data.DATA.RETURNCHARTANN) {
              this.returnChartAnn = response.data.content.lifecycle_data.DATA.RETURNCHARTANN
            }
            // Load lifecycle data (capital chart)
            if (response.data.content.lifecycle_data && response.data.content.lifecycle_data.DATA && response.data.content.lifecycle_data.DATA.CAPITALCHART) {
              this.capitalData = response.data.content.lifecycle_data.DATA.CAPITALCHART
            }
          }
        })
        .catch(error => {
          console.error('Error loading report:', error)
          this.internalEndpointError = true
        })
    },
    onProductAddedToPortfolio(portfolioIds) {
      this.addProductToPortfolio.show = false
      this.$bvToast.toast('Product Portfolio Updated', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })
      this.$store.dispatch('app/updatePortfolios')
      this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)
      this.$store.commit('app/ADD_UPDATED_PORTFOLIOS', portfolioIds)
    },
    createPayoffChart() {
      if (!this.figureTwo || !this.figureTwo.datapoints) return
      const datapointsArray = Object.values(this.figureTwo.datapoints)
      if (!datapointsArray || datapointsArray.length === 0) return
      const chartData = datapointsArray.map(point => ({
        x: point[0],
        y: point[1],
      }))
      const ctx = document.getElementById('PayoffChart')
      if (!ctx) return
      this.chart = new Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: [{
            data: chartData,
            backgroundColor: 'lightblue',
            pointStyle: 'circle',
            pointRadius: 4,
          }],
        },
        options: {
          legend: false,
          scales: {
            xAxes: [{
              type: 'linear',
              position: 'bottom',
              scaleLabel: {
                display: true,
                labelString: 'Final underlying value (%)',
              },
            }],
            yAxes: [{
              type: 'linear',
              position: 'bottom',
              scaleLabel: {
                display: true,
                labelString: 'Product payoff (%)',
              },
            }],
          },
          tooltips: {
            callbacks: {
              label(toolTipItem, data) {
                const dataset = data.datasets[toolTipItem.datasetIndex]
                const point = dataset.data[toolTipItem.index]
                return [`Underlying: ${point.x} %, Payoff ${point.y} %`]
              },
            },
            displayColors: false,
          },
        },
      })
    },
    createAnnualisedBarChart() {
      if (!this.figureSix || !this.figureSix.annualised || !this.figureSix.annualised.xAxisBuckets) return
      const ctx = document.getElementById('AnnualisedReturn')
      if (!ctx) return
      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.figureSix.annualised.xAxisBuckets,
          datasets: [{
            label: 'Annualised Return',
            data: this.figureSix.annualised.barData,
            backgroundColor: 'lightblue',
          }],
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                min: 0,
                max: 100,
              },
              scaleLabel: {
                display: true,
                labelString: 'Probabilities (%)',
              },
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Annualised return (%)',
              },
            }],
          },
          legend: {
            display: false,
          },
        },
      })
    },
    createPayoffBarChart() {
      if (!this.figureSix || !this.figureSix.payoff || !this.figureSix.payoff.xAxisBuckets) return
      const ctx = document.getElementById('ProductPayoffBarChart')
      if (!ctx) return
      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.figureSix.payoff.xAxisBuckets,
          datasets: [{
            label: 'Annualised Return',
            data: this.figureSix.payoff.barData,
            backgroundColor: 'lightblue',
          }],
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                min: 0,
                max: 100,
              },
              scaleLabel: {
                display: true,
                labelString: 'Probabilities (%)',
              },
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Product payoff (%)',
              },
            }],
          },
          legend: {
            display: false,
          },
        },
      })
    },
  },
}
</script>
